import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

/**
 * Crear un cliente en World Office desde la app.
 *
 * La aplicación encola y el worker crea el tercero allá. Es idempotente: si
 * el documento ya existe devuelve el que hay, así que reintentar nunca
 * duplica.
 */

export type TipoIdentificacion = {
  codigo: number
  sigla: string
  nombre: string
  es_empresa: boolean
  orden: number
}

export type SolicitudCliente = {
  outbox_id: string
  estado: 'pending' | 'done' | 'error'
  identificacion: string
  nombre: string
  wo_id_tercero: number | null
  ya_existia: boolean | null
  error_msg: string | null
  segundos: number
}

export function useTiposIdentificacion() {
  const consulta = useQuery({
    queryKey: ['tipos-identificacion'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<TipoIdentificacion[]> => {
      // Solo los que facturan en World Office (0263): cédula, NIT y cédula
      // de extranjería.
      const { data, error } = await supabase
        .from('tipos_identificacion')
        .select('*')
        .in('codigo', [13, 31, 22])
        .order('orden')

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as TipoIdentificacion[]
    },
  })

  return consulta.data ?? []
}

export function useCrearCliente() {
  return useMutation({
    mutationFn: async ({
      tipo,
      identificacion,
      nombre,
      apellidos,
      direccion,
      telefono,
      email,
      ciudad,
      propiedadRetencion,
    }: {
      tipo: number
      identificacion: string
      nombre: string
      apellidos?: string
      direccion?: string
      telefono?: string
      email?: string
      /** Código DANE del municipio. */
      ciudad?: number | null
      /** Régimen en World Office; null = el del tipo de documento. */
      propiedadRetencion?: number | null
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('fn_crear_cliente', {
        p_tipo_identificacion: tipo,
        p_identificacion: identificacion,
        p_nombre: nombre,
        p_apellidos: apellidos ?? null,
        p_direccion: direccion?.trim() || null,
        p_telefono: telefono?.trim() || null,
        p_email: email?.trim() || null,
        p_ciudad: ciudad ?? null,
        p_propiedad_retencion: propiedadRetencion ?? null,
      })

      if (error) throw new Error(mensajeDeError(error))

      return data as string
    },
  })
}

/**
 * Sigue la solicitud hasta que World Office responde.
 *
 * La primera creación tardó tres minutos y la segunda un segundo: el worker
 * puede estar dormido. Se consulta cada tres segundos y se deja de esperar a
 * los dos minutos, porque a partir de ahí el problema ya no es la espera.
 */
export function useEsperarCliente(outboxId: string | null) {
  const clienteConsultas = useQueryClient()

  // El comentario de arriba prometía rendirse a los dos minutos y el código no
  // lo hacía: la pantalla seguía preguntando cada tres segundos mientras
  // estuviera abierta. Mismo tope que useEsperarCotizacion.
  const inicio = useRef(Date.now())
  useEffect(() => {
    inicio.current = Date.now()
  }, [outboxId])

  const consulta = useQuery({
    queryKey: ['solicitud-cliente', outboxId],
    enabled: Boolean(outboxId),
    refetchInterval: (actual) => {
      const dato = actual.state.data as SolicitudCliente | undefined
      if (dato && dato.estado !== 'pending') return false
      if (Date.now() - inicio.current > 120_000) return false
      return 3000
    },
    queryFn: async (): Promise<SolicitudCliente | null> => {
      const { data, error } = await supabase
        .from('vista_solicitudes_cliente')
        .select(
          'outbox_id, estado, identificacion, nombre, wo_id_tercero, ya_existia, error_msg, segundos',
        )
        .eq('outbox_id', outboxId!)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))

      const solicitud = (data ?? null) as unknown as SolicitudCliente | null

      // Al llegar, el cliente ya está en las listas: se refrescan para que
      // aparezca sin recargar la pantalla.
      if (solicitud?.estado === 'done') {
        void clienteConsultas.invalidateQueries({ queryKey: ['clientes-wo'] })
      }

      return solicitud
    },
  })

  return consulta.data ?? null
}

/**
 * Las ciudades que World Office conoce, con su código DANE (0262). La más
 * usada primero: en MEGA casi todo es Cartago.
 */
export function useCiudadesWo() {
  const consulta = useQuery({
    queryKey: ['ciudades-wo'],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('fn_ciudades_wo')
      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as { id_ciudad: number; ciudad: string; clientes: number }[]
    },
  })
  return consulta.data ?? []
}
